import { useEffect } from 'react';
import { Refine } from '@refinedev/core';
import { useNotificationProvider } from '@refinedev/antd';
import routerProvider from '@refinedev/react-router';
import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react';
import type { MsalAuthenticationResult } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  Outlet,
} from 'react-router';
import { App as AntApp, Button, ConfigProvider } from 'antd';
import '@refinedev/antd/dist/reset.css';

import { msalInstance, API_SCOPE } from './auth/msalConfig';
import { SIGNED_OUT_KEY } from './auth/signedOutMarker';
import { dataProvider } from './api/dataProvider';
import { AppShell } from './components/AppShell';
import { RoleGuard } from './components/RoleGuard';
import { CrmGuard } from './components/CrmGuard';
import {
  ThemeModeProvider,
  useThemeMode,
  buildVendoiqTheme,
  brandColors,
  VENDOIQ_PALETTE,
} from '@viq-admin/theme';
import { Wordmark } from '@viq-admin/ui';

import { ConferenceList } from './resources/conferences/list';
import { ConferenceArchivedList } from './resources/conferences/archivedList';
import { CONFERENCE_ARCHIVE_ENABLED } from './config/features';
import { ConferenceCreate } from './resources/conferences/create';
import { ConferenceShow } from './resources/conferences/show';
import { UserList } from './resources/users/list';
import { UserCreate } from './resources/users/create';
import { LeadDetailView } from './views/LeadDetail/LeadDetailView';
import { LiveFeedRoute } from './views/LiveFeed/LiveFeedRoute';
import { UserDetailView } from './views/UserDetail/UserDetailView';
import { BatchRoute } from './views/BatchScreen/BatchRoute';
import { IntegrationsPage } from './views/Integrations/IntegrationsPage';
import { CompanySettingsPage } from './views/CompanySettings/CompanySettingsPage';
import { QuestionList } from './resources/questions/list';
import { QuestionCreate } from './resources/questions/create';
import { QuestionDetail } from './resources/questions/QuestionDetail';

// ---------------------------------------------------------------------------
// Auth gate — the Entra sign-in is itself branded, so there's no custom page
// ---------------------------------------------------------------------------
//
// A signed-out visitor is redirected straight to the Microsoft-hosted Entra
// sign-in, which is VendoIQ-branded (Company Branding + custom CSS). A
// collect-email-then-redirect page would only duplicate that branded screen
// and add a hop, so these two navy screens — shown for the instant before the
// redirect, and on a failed/cancelled sign-in — are the only pre-auth surfaces
// reached from INSIDE <MsalAuthenticationTemplate>.
//
// The exception is straight after an explicit sign-out: Entra's
// postLogoutRedirectUri is window.location.origin (msalConfig.ts) — this same
// app — so re-entering MsalAuthenticationTemplate there with zero accounts
// would auto-fire loginRedirect and bounce the user straight back to the
// Entra account picker (a sign-out "loop"). <SignedOut/> below is a third,
// passive navy screen rendered OUTSIDE the MsalAuthenticationTemplate gate
// (see the `signedOut` check in App()) so landing back here after logout
// stops instead of re-entering the gate.

function AuthLoading() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: brandColors.midnightNavy,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
      }}
    >
      <Wordmark height={64} />
      <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: 14 }}>
        Signing you in…
      </div>
    </div>
  );
}

/**
 * Shown when a sign-in attempt fails or is cancelled. Without an explicit error
 * branch, MsalAuthenticationTemplate would re-enter the redirect on the next
 * render and loop; a manual retry breaks that.
 */
function AuthError({ login }: MsalAuthenticationResult) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: brandColors.midnightNavy,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        padding: 24,
        textAlign: 'center',
      }}
    >
      <Wordmark height={64} />
      <div style={{ color: 'rgba(255, 255, 255, 0.75)', maxWidth: 360 }}>
        We couldn&apos;t complete sign-in. Please try again.
      </div>
      <Button
        type="primary"
        onClick={() => login(InteractionType.Redirect, { scopes: [API_SCOPE] })}
        style={{
          background: brandColors.signatureGold,
          borderColor: brandColors.signatureGold,
          color: VENDOIQ_PALETTE.navyDeep,
          fontWeight: 700,
        }}
      >
        Try again
      </Button>
    </div>
  );
}

/**
 * Rendered instead of <MsalAuthenticationTemplate> right after an explicit
 * sign-out (see the `signedOut` check in App(), below) — the passive
 * counterpart to AuthError/AuthLoading above. Its "Sign in" button clears the
 * marker itself before starting a fresh interactive login, the same request
 * shape a first-time visitor's auto-redirect would use.
 */
function SignedOut() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: brandColors.midnightNavy,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        padding: 24,
        textAlign: 'center',
      }}
    >
      <Wordmark height={64} />
      <div style={{ color: 'rgba(255, 255, 255, 0.75)', maxWidth: 360 }}>
        You&apos;ve been signed out.
      </div>
      <Button
        type="primary"
        onClick={() => {
          sessionStorage.removeItem(SIGNED_OUT_KEY);
          msalInstance.loginRedirect({ scopes: [API_SCOPE] });
        }}
        style={{
          background: brandColors.signatureGold,
          borderColor: brandColors.signatureGold,
          color: VENDOIQ_PALETTE.navyDeep,
          fontWeight: 700,
        }}
      >
        Sign in
      </Button>
    </div>
  );
}

// Wrapping in antd's <App> (aliased AntApp to avoid clashing with this
// module's own `App` component) mounts the App context so message/modal
// dialogs (e.g. useConfirmAction) render inside the configured theme instead
// of bypassing it — see packages/ui/src/useConfirmAction.tsx.
function ThemedApp({ children }: { children: React.ReactNode }) {
  const { mode } = useThemeMode();
  return (
    <ConfigProvider theme={buildVendoiqTheme(mode)}>
      <AntApp>{children}</AntApp>
    </ConfigProvider>
  );
}

export function App() {
  // Straight after an explicit sign-out, Entra's postLogoutRedirectUri lands
  // back on this exact origin (msalConfig.ts) with zero MSAL accounts. Left
  // to MsalAuthenticationTemplate alone, that reads as "never signed in" and
  // auto-fires loginRedirect — the sign-out loop. AppShell's onSignOut sets
  // SIGNED_OUT_KEY before calling logoutRedirect(); render the passive
  // <SignedOut/> screen instead of the gate while it's set and there's truly
  // no account.
  const accounts = msalInstance.getAllAccounts();
  const signedOut = accounts.length === 0 && sessionStorage.getItem(SIGNED_OUT_KEY) === '1';

  // Robustness: once an account is actually observed, scrub the marker. A
  // stale '1' left over from a past sign-out would otherwise keep matching on
  // every later render — including a FUTURE session expiry that has nothing
  // to do with an explicit sign-out — and incorrectly show "signed out"
  // instead of letting MsalAuthenticationTemplate auto-redirect to login the
  // way an expiry should.
  useEffect(() => {
    if (accounts.length > 0) {
      sessionStorage.removeItem(SIGNED_OUT_KEY);
    }
  }, [accounts.length]);

  return (
    <MsalProvider instance={msalInstance}>
      <ThemeModeProvider>
       <ThemedApp>
        {signedOut ? (
        <SignedOut />
        ) : (
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={{ scopes: [API_SCOPE] }}
          loadingComponent={AuthLoading}
          errorComponent={AuthError}
        >
          <BrowserRouter>
            <Refine
              dataProvider={dataProvider}
              routerProvider={routerProvider}
              notificationProvider={useNotificationProvider}
              resources={[
                {
                  name: 'conferences',
                  list: '/conferences',
                  create: '/conferences/new',
                  edit: '/conferences/:id/edit',
                  show: '/conferences/:id',
                  meta: { canDelete: false },
                },
                {
                  name: 'users',
                  list: '/users',
                  create: '/users/new',
                  edit: '/users/:id/edit',
                  meta: { canDelete: false },
                },
                {
                  name: 'questions',
                  list: '/questions',
                  create: '/questions/new',
                  edit: '/questions/:id/edit',
                  show: '/questions/:id',
                  meta: { canDelete: true, label: 'Questions Inventory' },
                },
                { name: 'leads', list: '/batch' },
              ]}
              options={{
                syncWithLocation: false,
                warnWhenUnsavedChanges: true,
                disableTelemetry: true,
                // Parity with the console QueryClient: a short staleTime + no
                // refetch-on-focus stops React Query's noisy defaults from
                // re-running the instantly-stale list query on ordinary
                // interactions (which blanked the DataTable's data for a frame
                // during a filter — the flicker). The DataTable's hasLoaded
                // guard already hides the flash; this removes the wasteful
                // refetches at the root.
                reactQuery: {
                  clientConfig: {
                    defaultOptions: {
                      queries: {
                        staleTime: 30_000,
                        refetchOnWindowFocus: false,
                      },
                    },
                  },
                },
              }}
            >
              <AppShell>
                <Routes>
                  <Route element={<Outlet />}>
                    <Route path="/" element={<Navigate to="/live-feed" />} />

                    <Route
                      path="/live-feed"
                      element={
                        <RoleGuard allow={['company_admin']}>
                          <LiveFeedRoute />
                        </RoleGuard>
                      }
                    />

                    {/* New Batch screen — replaces the legacy /leads flat-table view.
                        `/leads` (with no id) 301-equivalent redirects to /batch.
                        `/leads/:id` (lead detail) is untouched per spec. */}
                    <Route
                      path="/batch"
                      element={
                        <RoleGuard
                          allow={['company_admin', 'stakeholder']}
                        >
                          <BatchRoute />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/batch/:conferenceId"
                      element={
                        <RoleGuard
                          allow={['company_admin', 'stakeholder']}
                        >
                          <BatchRoute />
                        </RoleGuard>
                      }
                    />
                    <Route path="/leads" element={<Navigate to="/batch" replace />} />
                    <Route
                      path="/leads/:id"
                      element={
                        <RoleGuard
                          allow={['company_admin', 'stakeholder']}
                        >
                          <LeadDetailView />
                        </RoleGuard>
                      }
                    />

                    <Route
                      path="/conferences"
                      element={
                        <RoleGuard allow={['company_admin']}>
                          <ConferenceList />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/conferences/archived"
                      element={
                        CONFERENCE_ARCHIVE_ENABLED ? (
                          <RoleGuard allow={['company_admin']}>
                            <ConferenceArchivedList />
                          </RoleGuard>
                        ) : (
                          <Navigate to="/conferences" replace />
                        )
                      }
                    />
                    <Route
                      path="/conferences/new"
                      element={
                        <RoleGuard allow={['company_admin']}>
                          <ConferenceCreate />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/conferences/:id/edit"
                      element={<Navigate to="../" relative="path" replace />}
                    />
                    <Route
                      path="/conferences/:id"
                      element={
                        <RoleGuard allow={['company_admin']}>
                          <ConferenceShow />
                        </RoleGuard>
                      }
                    />

                    <Route
                      path="/users"
                      element={
                        <RoleGuard allow={['company_admin']}>
                          <UserList />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/users/new"
                      element={
                        <RoleGuard allow={['company_admin']}>
                          <UserCreate />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/users/:oid"
                      element={
                        <RoleGuard allow={['company_admin']}>
                          <UserDetailView />
                        </RoleGuard>
                      }
                    />

                    <Route
                      path="/questions"
                      element={
                        <RoleGuard allow={['company_admin']}>
                          <QuestionList />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/questions/new"
                      element={
                        <RoleGuard allow={['company_admin']}>
                          <QuestionCreate />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/questions/:id/edit"
                      element={<Navigate to="../" relative="path" replace />}
                    />
                    <Route
                      path="/questions/:id"
                      element={
                        <RoleGuard allow={['company_admin']}>
                          <QuestionDetail />
                        </RoleGuard>
                      }
                    />

                    <Route
                      path="/integrations"
                      element={
                        <RoleGuard allow={['company_admin']}>
                          <CrmGuard>
                            <IntegrationsPage />
                          </CrmGuard>
                        </RoleGuard>
                      }
                    />

                    <Route
                      path="/settings"
                      element={
                        <RoleGuard allow={['company_admin']}>
                          <CompanySettingsPage />
                        </RoleGuard>
                      }
                    />

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                </Routes>
              </AppShell>
            </Refine>
          </BrowserRouter>
        </MsalAuthenticationTemplate>
        )}
       </ThemedApp>
      </ThemeModeProvider>
    </MsalProvider>
  );
}
